import React, { useMemo, useState, useEffect } from "react";
import "./ppf.css";
import ProfilePictureUpload from "./ProfilePictureUpload";
import axios from "axios";
import { ApiConnectionString } from "../../../constants/ApiConntection";
import AddQualification from "./AddQualification";
import { Stars, ArrowRight, CheckCircle } from "react-bootstrap-icons";

const initialForm = {
  showPhone: false,
  showEmail: true,

  neurodivergentInformed: false,
  neurodivergentSpecialist: false,
  adhdInformed: false,
  adhdSpecialist: false,

  acceptedProfileImageTerms: false,
  profileImageUploaded: false,

  hasAtLeastOneQualification: false,

  displayName: "",
  howIWork: "",
  headLine: "",
  email: "",
  firstName: "",
  lastName: "",
  aboutMe: "",
  websiteUrl: "",
  noWebsite: false,
  contactNumber: "",
  platforms: "",

  sessionLength: 0,
  pricePerSession: 0,

  online: false,
  phone: false,

  offersInPerson: false,
  postcode: "",
  townCity: "",

  worksWithAdults: false,
  worksWithChildren: false,
  worksWithAdolescents: false,

  cbt: false,
  act: false,
  dbt: false,
  psychodynamic: false,
  personCentred: false,
  integrative: false,
  solutionFocused: false,
  traumaInformed: false,
  adhdCoaching: false,
  autismAffirming: false,
  neurodiversityAffirming: false,
  talkingTherapy: false,
  psychosexual: false,
  adhd: false,
  autism: false,
  anxiety: false,
  burnout: false,
  emotionalRegulation: false,
  maskingAndPeoplePleasing: false,
  selfEsteemAndIdentity: false,
  relationshipsAndCommunication: false,
  workplaceStressAndAdjustments: false,
  traumaInformedSupport: false,
  lateDiagnosisSupport: false,
  sensoryProcessing: false,

  professionalBody: "",
  registrationNumber: "",
  usState: "",
  usLicenseType: "",
  usLicenseNumber: "",

  mondayMorning: false,
  mondayAfternoon: false,
  mondayEvening: false,
  tuesdayMorning: false,
  tuesdayAfternoon: false,
  tuesdayEvening: false,
  wednesdayMorning: false,
  wednesdayAfternoon: false,
  wednesdayEvening: false,
  thursdayMorning: false,
  thursdayAfternoon: false,
  thursdayEvening: false,
  fridayMorning: false,
  fridayAfternoon: false,
  fridayEvening: false,
  saturdayMorning: false,
  saturdayAfternoon: false,
  saturdayEvening: false,
  sundayMorning: false,
  sundayAfternoon: false,
  sundayEvening: false,
};

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled = false,
  required = false,
  error,
}) {
  return (
    <div className="mb-3 ppfField">
      <label className="form-label ppfLabel" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        className={`form-control ppfInput${error ? " is-invalid" : ""}`}
        name={name}
        value={value || ""}
        onChange={onChange}
        type={type}
        placeholder={placeholder || ""}
        disabled={disabled}
        required={required}
      />
      {error ? <div className="invalid-feedback">{error}</div> : null}
    </div>
  );
}

function TextArea({ label, name, value, onChange, rows = 4, error }) {
  return (
    <div className="mb-3 ppfField">
      <label className="form-label ppfLabel" htmlFor={name}>
        {label}
      </label>
      <textarea
        id={name}
        className={`form-control ppfInput${error ? " is-invalid" : ""}`}
        name={name}
        value={value || ""}
        onChange={onChange}
        rows={rows}
      />
      {error ? <div className="invalid-feedback">{error}</div> : null}
    </div>
  );
}

function Check({ label, name, checked, onChange }) {
  return (
    <div className="form-check mb-2">
      <input
        className="form-check-input ppfCheck"
        type="checkbox"
        id={name}
        name={name}
        checked={!!checked}
        onChange={onChange}
      />
      <label className="form-check-label ppfLabel" htmlFor={name}>
        {label}
      </label>
    </div>
  );
}

function GroupCard({ title, hint, children }) {
  return (
    <div className="ppfGroupCard">
      <div className="ppfGroupCardHead">
        <div>
          <div className="ppfGroupCardTitle">{title}</div>
          {hint ? <div className="ppfSectionHint mb-0">{hint}</div> : null}
        </div>
      </div>
      <div className="ppfGroupCardBody">{children}</div>
    </div>
  );
}

function CheckGrid({ title, hint, items, form, onToggle }) {
  return (
    <GroupCard title={title} hint={hint}>
      <div className="row">
        {items.map((it) => (
          <div className="col-12 col-md-6 col-lg-4" key={it.name}>
            <Check
              label={it.label}
              name={it.name}
              checked={form[it.name]}
              onChange={onToggle}
            />
          </div>
        ))}
      </div>
    </GroupCard>
  );
}

export default function PractitionerProfileForm({
  value,
  user,
  onSubmit,
  fetchData,
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(value || initialForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");

  const steps = useMemo(
    () => [
      { title: "Welcome" },
      { title: "Profile basics" },
      { title: "Profile image" },
      { title: "About and approach" },
      { title: "Focus and experience" },
      { title: "Expertise" },
      { title: "Sessions and format" },
      { title: "Availability" },
      { title: "Contact" },
      { title: "Professional registration" },
      { title: "Qualifications" },
      { title: "Complete" },
    ],
    [],
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setStep((s) => {
      const max = steps.length - 1;
      if (s < 0) return 0;
      if (s > max) return max;
      return s;
    });
  }, [steps.length]);

  useEffect(() => {
    scrollToTop();
    setErrors({});
  }, [step]);

  const clearError = (name) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

 const onChange = (e) => {
  const { name, value: v } = e.target;
  setForm((prev) => ({ ...prev, [name]: v }));
  clearError(name);
  setApiError("");
};

  const onToggle = (e) => {
    const { name, checked } = e.target;
      setApiError("");

    setForm((prev) => {
      if (name === "noWebsite") {
        return {
          ...prev,
          noWebsite: checked,
          websiteUrl: checked ? "" : prev.websiteUrl,
        };
      }

      if (name === "adhdInformed" && checked) {
        return { ...prev, adhdInformed: true, adhdSpecialist: false };
      }

      if (name === "adhdSpecialist" && checked) {
        return { ...prev, adhdSpecialist: true, adhdInformed: false };
      }

      if (name === "neurodivergentInformed" && checked) {
        return {
          ...prev,
          neurodivergentInformed: true,
          neurodivergentSpecialist: false,
        };
      }

      if (name === "neurodivergentSpecialist" && checked) {
        return {
          ...prev,
          neurodivergentSpecialist: true,
          neurodivergentInformed: false,
        };
      }

      return { ...prev, [name]: checked };
    });

    clearError(name);
  };

  const emptyIfNull = (v) => (v === null || v === undefined ? "" : v);

  const toNumberOrZero = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const toDecimalOrZero = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const buildPayload = () => {
    return {
      showPhone: !!form.showPhone,
      showEmail: !!form.showEmail,

      neurodivergentInformed: !!form.neurodivergentInformed,
      neurodivergentSpecialist: !!form.neurodivergentSpecialist,
      adhdInformed: !!form.adhdInformed,
      adhdSpecialist: !!form.adhdSpecialist,

      displayName: emptyIfNull(form.displayName),
      howIWork: emptyIfNull(form.howIWork),
      headLine: emptyIfNull(form.headLine),
      contactEmail: emptyIfNull(form.email),
      firstName: emptyIfNull(form.firstName),
      lastName: emptyIfNull(form.lastName),
      aboutMe: emptyIfNull(form.aboutMe),
      websiteUrl: form.noWebsite ? "" : emptyIfNull(form.websiteUrl),
      contactNumber: emptyIfNull(form.contactNumber),
      platforms: emptyIfNull(form.platforms),

      online: !!form.online,
      phone: !!form.phone,

      offersInPerson: !!form.offersInPerson,
      postcode: emptyIfNull(form.postcode),
      townCity: emptyIfNull(form.townCity),

      worksWithAdults: !!form.worksWithAdults,
      worksWithChildren: !!form.worksWithChildren,
      worksWithAdolescents: !!form.worksWithAdolescents,

      cbt: !!form.cbt,
      act: !!form.act,
      dbt: !!form.dbt,
      psychodynamic: !!form.psychodynamic,
      personCentred: !!form.personCentred,
      integrative: !!form.integrative,
      solutionFocused: !!form.solutionFocused,
      traumaInformed: !!form.traumaInformed,
      adhdCoaching: !!form.adhdCoaching,
      autismAffirming: !!form.autismAffirming,
      neurodiversityAffirming: !!form.neurodiversityAffirming,
      talkingTherapy: !!form.talkingTherapy,
      psychosexual: !!form.psychosexual,
      adhd: !!form.adhd,
      autism: !!form.autism,
      anxiety: !!form.anxiety,
      burnout: !!form.burnout,
      emotionalRegulation: !!form.emotionalRegulation,
      maskingAndPeoplePleasing: !!form.maskingAndPeoplePleasing,
      selfEsteemAndIdentity: !!form.selfEsteemAndIdentity,
      relationshipsAndCommunication: !!form.relationshipsAndCommunication,
      workplaceStressAndAdjustments: !!form.workplaceStressAndAdjustments,
      traumaInformedSupport: !!form.traumaInformedSupport,
      lateDiagnosisSupport: !!form.lateDiagnosisSupport,
      sensoryProcessing: !!form.sensoryProcessing,

      professionalBody:
        value?.country === "UK" ? emptyIfNull(form.professionalBody) : "",
      registrationNumber:
        value?.country === "UK" ? emptyIfNull(form.registrationNumber) : "",

      state: value?.country === "US" ? emptyIfNull(form.usState) : "",
      licenseType:
        value?.country === "US" ? emptyIfNull(form.usLicenseType) : "",
      licenseNumber:
        value?.country === "US" ? emptyIfNull(form.usLicenseNumber) : "",

      mondayMorning: !!form.mondayMorning,
      mondayAfternoon: !!form.mondayAfternoon,
      mondayEvening: !!form.mondayEvening,
      tuesdayMorning: !!form.tuesdayMorning,
      tuesdayAfternoon: !!form.tuesdayAfternoon,
      tuesdayEvening: !!form.tuesdayEvening,
      wednesdayMorning: !!form.wednesdayMorning,
      wednesdayAfternoon: !!form.wednesdayAfternoon,
      wednesdayEvening: !!form.wednesdayEvening,
      thursdayMorning: !!form.thursdayMorning,
      thursdayAfternoon: !!form.thursdayAfternoon,
      thursdayEvening: !!form.thursdayEvening,
      fridayMorning: !!form.fridayMorning,
      fridayAfternoon: !!form.fridayAfternoon,
      fridayEvening: !!form.fridayEvening,
      saturdayMorning: !!form.saturdayMorning,
      saturdayAfternoon: !!form.saturdayAfternoon,
      saturdayEvening: !!form.saturdayEvening,
      sundayMorning: !!form.sundayMorning,
      sundayAfternoon: !!form.sundayAfternoon,
      sundayEvening: !!form.sundayEvening,

      sessionLength: toNumberOrZero(form.sessionLength),
      pricePerSession: toDecimalOrZero(form.pricePerSession),
    };
  };

const update = async (payload) => {
  try {
    await axios.put(`${ApiConnectionString}/api/counsellorProfile`, payload, {
      headers: { Authorization: `bearer ${user.token}` },
    });

    setApiError("");
    return true;
  } catch (error) {
    setApiError(
      error?.response?.data || "Something went wrong. Please try again."
    );
    scrollToTop();
    return false;
  }
};

  const completeProfile = async (payload) => {
    const res = await axios.post(
      `${ApiConnectionString}/api/counsellorProfile/complete`,
      payload,
      {
        headers: { Authorization: `bearer ${user.token}` },
      },
    );

    if (res.status == 200) {
      fetchData();
    }
  };

const submit = async (e) => {
  e.preventDefault();

  const payload = buildPayload();
  const ok = await update(payload);

  if (!ok) return;

  if (onSubmit) onSubmit(payload);
};

  const isBlank = (v) => String(v ?? "").trim() === "";

  const validateStep = () => {
    const nextErrors = {};

    if (step === 1) {
      if (isBlank(form.displayName)) nextErrors.displayName = "Required";
      if (isBlank(form.headLine)) nextErrors.headLine = "Required";
    }

    if (step === 2) {
      if (!form.profileImageUploaded)
        nextErrors.profileImageUploaded = "Upload a photo";
      if (!form.acceptedProfileImageTerms)
        nextErrors.acceptedProfileImageTerms = "Required";
    }

    if (step === 3) {
      if (isBlank(form.aboutMe)) nextErrors.aboutMe = "Required";
      if (isBlank(form.howIWork)) nextErrors.howIWork = "Required";
      if (!form.noWebsite && isBlank(form.websiteUrl))
        nextErrors.websiteUrl = "Required";
    }

    if (step === 4) {
      if (
        !form.neurodivergentInformed &&
        !form.neurodivergentSpecialist &&
        !form.adhdInformed &&
        !form.adhdSpecialist
      ) {
        nextErrors.focusLabels = "Select at least one option";
      }
    }

    if (step === 5) {
      if (
        !form.worksWithAdults &&
        !form.worksWithAdolescents &&
        !form.worksWithChildren
      ) {
        nextErrors.worksWith = "Select at least one option";
      }

      const anyTherapy =
        form.cbt ||
        form.act ||
        form.dbt ||
        form.psychodynamic ||
        form.personCentred ||
        form.integrative ||
        form.solutionFocused ||
        form.traumaInformed ||
        form.adhdCoaching ||
        form.autismAffirming ||
        form.neurodiversityAffirming ||
        form.psychosexual ||
        form.talkingTherapy;

      if (!anyTherapy) nextErrors.therapyTypes = "Select at least one option";

      const anySupport =
        form.adhd ||
        form.autism ||
        form.anxiety ||
        form.burnout ||
        form.emotionalRegulation ||
        form.maskingAndPeoplePleasing ||
        form.selfEsteemAndIdentity ||
        form.relationshipsAndCommunication ||
        form.workplaceStressAndAdjustments ||
        form.traumaInformedSupport ||
        form.lateDiagnosisSupport ||
        form.sensoryProcessing;

      if (!anySupport) nextErrors.areasOfSupport = "Select at least one option";
    }

    if (step === 6) {
      if (!form.online && !form.phone && !form.offersInPerson)
        nextErrors.supportFormat = "Select at least one option";

      if (form.offersInPerson && isBlank(form.postcode))
        nextErrors.postcode = "Required";

      if (toNumberOrZero(form.sessionLength) <= 0)
        nextErrors.sessionLength = "Required";

      if (toDecimalOrZero(form.pricePerSession) <= 0)
        nextErrors.pricePerSession = "Required";

      if (form.online && isBlank(form.platforms))
        nextErrors.platforms = "Required";
    }

    if (step === 7) {
      const anyAvailability =
        form.mondayMorning ||
        form.mondayAfternoon ||
        form.mondayEvening ||
        form.tuesdayMorning ||
        form.tuesdayAfternoon ||
        form.tuesdayEvening ||
        form.wednesdayMorning ||
        form.wednesdayAfternoon ||
        form.wednesdayEvening ||
        form.thursdayMorning ||
        form.thursdayAfternoon ||
        form.thursdayEvening ||
        form.fridayMorning ||
        form.fridayAfternoon ||
        form.fridayEvening ||
        form.saturdayMorning ||
        form.saturdayAfternoon ||
        form.saturdayEvening ||
        form.sundayMorning ||
        form.sundayAfternoon ||
        form.sundayEvening;

      if (!anyAvailability)
        nextErrors.availability = "Select at least one time slot";
    }

    if (step === 8) {
      if (isBlank(form.contactNumber)) nextErrors.contactNumber = "Required";
      if (isBlank(form.email)) nextErrors.email = "Required";
    }

    if (step === 9) {
      if (value?.country === "UK") {
        if (isBlank(form.professionalBody))
          nextErrors.professionalBody = "Required";
        if (isBlank(form.registrationNumber))
          nextErrors.registrationNumber = "Required";
      }
      if (value?.country === "US") {
        if (isBlank(form.usState)) nextErrors.usState = "Required";
        if (isBlank(form.usLicenseType)) nextErrors.usLicenseType = "Required";
        if (isBlank(form.usLicenseNumber))
          nextErrors.usLicenseNumber = "Required";
      }
    }

    if (step === 10) {
      if (!form.hasAtLeastOneQualification)
        nextErrors.hasAtLeastOneQualification =
          "Add at least one qualification";
    }

    const ok = Object.keys(nextErrors).length === 0;
    setErrors(nextErrors);
    if (!ok) scrollToTop();
    return ok;
  };

const handleNext = async () => {
  if (!validateStep()) return;

  const payload = buildPayload();
  const ok = await update(payload);

  if (!ok) return;

  setStep((s) => {
    const next = s + 1;
    const max = steps.length - 1;
    return next > max ? max : next;
  });
};

  const handleBack = () => {
    setStep((s) => (s - 1 < 0 ? 0 : s - 1));
  };

 const handleComplete = async () => {
  if (!validateStep()) return;

  const payload = buildPayload();
  const ok = await update(payload);

  if (!ok) return;

  await completeProfile(payload);
  if (onSubmit) onSubmit(payload);
};

  const focusItems = [
    { name: "neurodivergentInformed", label: "Neurodivergent Affirming" },
    { name: "neurodivergentSpecialist", label: "Neurodivergent specialist" },
    { name: "adhdInformed", label: "ADHD Affirming" },
    { name: "adhdSpecialist", label: "ADHD specialist" },
  ];

  const worksWithItems = [
    { name: "worksWithAdults", label: "Adults" },
    { name: "worksWithAdolescents", label: "Adolescents" },
    { name: "worksWithChildren", label: "Children" },
  ];

  const therapyItems = [
    { name: "cbt", label: "CBT" },
    { name: "act", label: "ACT" },
    { name: "dbt", label: "DBT" },
    { name: "psychodynamic", label: "Psychodynamic" },
    { name: "personCentred", label: "Person centred" },
    { name: "integrative", label: "Integrative" },
    { name: "solutionFocused", label: "Solution focused" },
    { name: "traumaInformed", label: "Trauma informed" },
    { name: "adhdCoaching", label: "ADHD coaching" },
    { name: "autismAffirming", label: "Autism affirming" },
    { name: "neurodiversityAffirming", label: "Neurodiversity affirming" },
    { name: "talkingTherapy", label: "Talking therapy" },
    { name: "psychosexual", label: "Psychosexual" },
  ];

  const supportItems = [
    { name: "adhd", label: "ADHD" },
    { name: "autism", label: "Autism" },
    { name: "anxiety", label: "Anxiety" },
    { name: "burnout", label: "Burnout" },
    { name: "emotionalRegulation", label: "Emotional regulation" },
    { name: "maskingAndPeoplePleasing", label: "Masking and people pleasing" },
    { name: "selfEsteemAndIdentity", label: "Self esteem and identity" },
    {
      name: "relationshipsAndCommunication",
      label: "Relationships and communication",
    },
    {
      name: "workplaceStressAndAdjustments",
      label: "Workplace stress and adjustments",
    },
    { name: "traumaInformedSupport", label: "Trauma informed support" },
    { name: "lateDiagnosisSupport", label: "Late diagnosis support" },
    { name: "sensoryProcessing", label: "Sensory processing" },
  ];

  const availabilityDays = [
    {
      day: "Monday",
      keys: ["mondayMorning", "mondayAfternoon", "mondayEvening"],
    },
    {
      day: "Tuesday",
      keys: ["tuesdayMorning", "tuesdayAfternoon", "tuesdayEvening"],
    },
    {
      day: "Wednesday",
      keys: ["wednesdayMorning", "wednesdayAfternoon", "wednesdayEvening"],
    },
    {
      day: "Thursday",
      keys: ["thursdayMorning", "thursdayAfternoon", "thursdayEvening"],
    },
    {
      day: "Friday",
      keys: ["fridayMorning", "fridayAfternoon", "fridayEvening"],
    },
    {
      day: "Saturday",
      keys: ["saturdayMorning", "saturdayAfternoon", "saturdayEvening"],
    },
    {
      day: "Sunday",
      keys: ["sundayMorning", "sundayAfternoon", "sundayEvening"],
    },
  ];

  const renderStep = () => {
    if (step === 0) {
      return (
        <GroupCard
          title="Welcome"
          hint="We will guide you through setting up your profile."
        >
          <div className="ppfWelcome">
            <div className="ppfWelcomeLead">
              Thank you for joining Neurodiverse Therapy. This form helps you
              create a profile that feels right for you and helps clients
              understand how you work.
            </div>

            <div className="ppfWelcomeList">
              <div className="ppfWelcomeItem">
                <span className="ppfWelcomeIcon">
                  <Stars />
                </span>
                <div className="ppfWelcomeText">
                  You do not need to do everything at once. You can move at your
                  own pace and come back anytime.
                </div>
              </div>

              <div className="ppfWelcomeItem">
                <span className="ppfWelcomeIcon">
                  <ArrowRight />
                </span>
                <div className="ppfWelcomeText">
                  We will guide you step by step and keep things simple.
                </div>
              </div>

              <div className="ppfWelcomeItem">
                <span className="ppfWelcomeIcon">
                  <CheckCircle />
                </span>
                <div className="ppfWelcomeText">
                  When you are finished, we will review your profile and let you
                  know when it is ready to go live.
                </div>
              </div>
            </div>
          </div>
        </GroupCard>
      );
    }

    if (step === 1) {
      return (
        <>
          <Field
            label="Display name"
            name="displayName"
            value={form.displayName}
            onChange={onChange}
            error={errors.displayName}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            Use your professional full name exactly as you would like it shown
            to clients. You can include titles such as Dr, and letters of
            qualification. Your first name can be abbreviated if you prefer.
          </small>

          <Field
            label="Headline"
            name="headLine"
            value={form.headLine}
            onChange={onChange}
            error={errors.headLine}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            A short title that appears under your name when clients are browsing
            therapists. This could be your highest level of qualification or
            your specialism.
          </small>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <div className="row">
            <ProfilePictureUpload
              user={user}
              accepted={form.acceptedProfileImageTerms}
              onToggle={onToggle}
              error={errors.acceptedProfileImageTerms}
              onImageStatusChange={(hasImage) => {
                setForm((prev) => ({
                  ...prev,
                  profileImageUploaded: !!hasImage,
                }));
                if (hasImage) clearError("profileImageUploaded");
              }}
              imageError={errors.profileImageUploaded}
            />
          </div>
        </>
      );
    }

    if (step === 3) {
      return (
        <>
          <TextArea
            label="About me"
            name="aboutMe"
            value={form.aboutMe}
            onChange={onChange}
            error={errors.aboutMe}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            This is your space to introduce yourself as a person. You might
            share your background, lived experience, values, or what led you to
            your work. Write in your own voice and at your own pace.
          </small>

          <TextArea
            label="How I work"
            name="howIWork"
            value={form.howIWork}
            onChange={onChange}
            error={errors.howIWork}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            Explain what clients can expect from working with you. For example
            your approach, session style, structure, or how you support
            neurodivergent clients. This helps people decide if you feel like a
            good fit.
          </small>

          <Check
            label="I dont have a website"
            name="noWebsite"
            checked={form.noWebsite}
            onChange={onToggle}
          />

          <Field
            label="Website URL"
            name="websiteUrl"
            value={form.websiteUrl}
            onChange={onChange}
            disabled={form.noWebsite}
            error={errors.websiteUrl}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            If you have a website, share the link here so clients can learn more
            about you. This is optional.
          </small>
        </>
      );
    }

    if (step === 4) {
      return (
        <>
          <CheckGrid
            title="Focus and experience"
            hint="Choose the labels that best describe your work. You can be either affirming or a specialist, but not both."
            items={focusItems}
            form={form}
            onToggle={onToggle}
          />

          {errors.focusLabels ? (
            <div className="invalid-feedback d-block mt-2">
              {errors.focusLabels}
            </div>
          ) : null}

          <div className="ppfGroupCard mt-3">
            <div className="ppfGroupCardHead">
              <p className="ppfGroupCardTitle mb-0">What these labels mean</p>
              <span className="ppfInfoPill">Helpful guide</span>
            </div>

            <div className="ppfGroupCardBody">
              <div className="ppfDefGrid">
                <div className="ppfDefCard">
                  <div className="ppfDefTop">
                    <span className="ppfDefTitle">Neurodiverse Affirming</span>
                    <span className="ppfDefTag">Experience</span>
                  </div>
                  <p className="ppfDefText mb-0">
                    You are not a specialist, but you have experience working
                    with neurodivergent people and an understanding of
                    neurodiversity that helps you work in an inclusive and
                    supportive way.
                  </p>
                </div>

                <div className="ppfDefCard">
                  <div className="ppfDefTop">
                    <span className="ppfDefTitle">Neurodiverse Specialist</span>
                    <span className="ppfDefTag ppfDefTagStrong">
                      Specialist
                    </span>
                  </div>
                  <p className="ppfDefText mb-0">
                    You have specific training or qualifications focused on
                    neurodiversity and specialise in understanding and working
                    with neurodivergent people.
                  </p>
                </div>

                <div className="ppfDefCard">
                  <div className="ppfDefTop">
                    <span className="ppfDefTitle">ADHD Affirming</span>
                    <span className="ppfDefTag">Experience</span>
                  </div>
                  <p className="ppfDefText mb-0">
                    You are not an ADHD specialist, but you have experience
                    working with people with ADHD and adapt how you work to
                    support their needs.
                  </p>
                </div>

                <div className="ppfDefCard">
                  <div className="ppfDefTop">
                    <span className="ppfDefTitle">ADHD Specialist</span>
                    <span className="ppfDefTag ppfDefTagStrong">
                      Specialist
                    </span>
                  </div>
                  <p className="ppfDefText mb-0">
                    You have formal training or qualifications related to ADHD
                    and specialise in working with people with ADHD.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (step === 5) {
      return (
        <>
          <CheckGrid
            title="Works with"
            hint="Select the age groups you work with."
            items={worksWithItems}
            form={form}
            onToggle={onToggle}
          />
          {errors.worksWith ? (
            <div className="invalid-feedback d-block mt-2">
              {errors.worksWith}
            </div>
          ) : null}

          <CheckGrid
            title="Therapy types"
            hint="Select any modalities you offer."
            items={therapyItems}
            form={form}
            onToggle={onToggle}
          />
          {errors.therapyTypes ? (
            <div className="invalid-feedback d-block mt-2">
              {errors.therapyTypes}
            </div>
          ) : null}

          <CheckGrid
            title="Areas of support"
            hint="Select any areas you support."
            items={supportItems}
            form={form}
            onToggle={onToggle}
          />
          {errors.areasOfSupport ? (
            <div className="invalid-feedback d-block mt-2">
              {errors.areasOfSupport}
            </div>
          ) : null}
        </>
      );
    }

    if (step === 6) {
      return (
        <>
          <GroupCard
            title="Support format"
            hint="How do you usually offer sessions? Select all that apply. "
          >
            <p className="text-muted">
              Online means you accept remote sessions via apps such as Zoom or
              Microsoft Teams. Phone means you accept sessions over the phone,
              either landline or mobile.
            </p>
            <Check
              label="Online"
              name="online"
              checked={form.online}
              onChange={onToggle}
            />
            <Check
              label="Phone"
              name="phone"
              checked={form.phone}
              onChange={onToggle}
            />
            {errors.supportFormat ? (
              <div className="invalid-feedback d-block">
                {errors.supportFormat}
              </div>
            ) : null}

            <Check
              label="In person"
              name="offersInPerson"
              checked={form.offersInPerson}
              onChange={onToggle}
            />

       {form.offersInPerson ? (
  <>
    <Field
      label="Town / city"
      name="townCity"
      value={form.townCity}
      onChange={onChange}
      placeholder="eg Manchester"
    />

    <small className="text-muted d-block mb-3 ppfHelpText">
      Enter the town or city you want shown on your public profile.
    </small>

    <Field
      label="Practice postcode"
      name="postcode"
      value={form.postcode}
      onChange={onChange}
      placeholder="eg SW1A 1AA"
      error={errors.postcode}
    />

    {apiError ? (
      <div className="invalid-feedback d-block">{apiError}</div>
    ) : null}

  <div className="bcs-subtitle">
  Enter your full postcode to help clients find therapists near
  them. Your postcode is never shown publicly and is only used
  for location-based search and distance matching.
</div>
  </>
) : null}
          </GroupCard>

          <div className="row">
            <div className="col-12 col-md-6">
              <Field
                label="Session length (minutes)"
                name="sessionLength"
                value={form.sessionLength}
                onChange={onChange}
                type="number"
                error={errors.sessionLength}
              />
              <small className="text-muted d-block mb-3 ppfHelpText">
                The usual length of a standard session.
              </small>
            </div>

            <div className="col-12 col-md-6">
              <Field
                label="Platforms"
                name="platforms"
                value={form.platforms}
                onChange={onChange}
                placeholder="eg Zoom, Microsoft Teams, Google Meet, WhatsApp"
                error={errors.platforms}
              />
              <small className="text-muted d-block mb-3 ppfHelpText">
                List the platforms you use for online sessions.
              </small>
            </div>

            <div className="col-12 col-md-6">
              <div className="mb-3 ppfField">
                <label className="form-label ppfLabel">Price per session</label>
                <div
                  className={`input-group ppfInputGroup${errors.pricePerSession ? " is-invalid" : ""}`}
                >
                  <span className="input-group-text ppfInputGroupText">
                    {value?.country == "UK" ? "£" : "$"}
                  </span>
                  <input
                    type="number"
                    className={`form-control ppfInput${errors.pricePerSession ? " is-invalid" : ""}`}
                    name="pricePerSession"
                    value={form.pricePerSession}
                    onChange={onChange}
                  />
                  {errors.pricePerSession ? (
                    <div className="invalid-feedback">
                      {errors.pricePerSession}
                    </div>
                  ) : null}
                </div>
              </div>
              <small className="text-muted d-block mb-3 ppfHelpText">
                Your standard session fee.
              </small>
            </div>
          </div>
        </>
      );
    }

    if (step === 7) {
      return (
        <GroupCard title="Availability">
          <div className="ppfAvailabilityWrap">
            <div className="mb-3">
              <div className="fw-semibold">
                Select the times you are usually available
              </div>
              <div className="text-muted">
                This does not have to be exact or the same every week. It is
                just to give clients a clear idea of when your routine generally
                matches theirs.
              </div>
              {errors.availability ? (
                <div className="invalid-feedback d-block mt-2">
                  {errors.availability}
                </div>
              ) : null}
            </div>

            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className="table align-middle mb-0 ppfAvailabilityTable">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Morning</th>
                      <th>Afternoon</th>
                      <th>Evening</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availabilityDays.map((d) => (
                      <tr key={d.day}>
                        <td className="ppfAvailabilityDay">{d.day}</td>
                        <td>
                          <input
                            className="form-check-input ppfCheck"
                            type="checkbox"
                            name={d.keys[0]}
                            checked={!!form[d.keys[0]]}
                            onChange={onToggle}
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input ppfCheck"
                            type="checkbox"
                            name={d.keys[1]}
                            checked={!!form[d.keys[1]]}
                            onChange={onToggle}
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input ppfCheck"
                            type="checkbox"
                            name={d.keys[2]}
                            checked={!!form[d.keys[2]]}
                            onChange={onToggle}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="d-md-none">
              {availabilityDays.map((d) => (
                <div className="ppfAvailabilityCard" key={d.day}>
                  <div className="ppfAvailabilityCardDay">{d.day}</div>

                  <div className="ppfAvailabilitySlots">
                    <label className="ppfAvailabilitySlot">
                      <input
                        className="form-check-input ppfCheck"
                        type="checkbox"
                        name={d.keys[0]}
                        checked={!!form[d.keys[0]]}
                        onChange={onToggle}
                      />
                      <span>Morning</span>
                    </label>

                    <label className="ppfAvailabilitySlot">
                      <input
                        className="form-check-input ppfCheck"
                        type="checkbox"
                        name={d.keys[1]}
                        checked={!!form[d.keys[1]]}
                        onChange={onToggle}
                      />
                      <span>Afternoon</span>
                    </label>

                    <label className="ppfAvailabilitySlot">
                      <input
                        className="form-check-input ppfCheck"
                        type="checkbox"
                        name={d.keys[2]}
                        checked={!!form[d.keys[2]]}
                        onChange={onToggle}
                      />
                      <span>Evening</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </GroupCard>
      );
    }

    if (step === 8) {
      return (
        <>
          <GroupCard
            title="Visibility"
            hint="Choose what contact details appear on your public profile. If you turn these off, people can still contact you through the directory if you offer messaging."
          >
            <Check
              label="Show phone number on profile"
              name="showPhone"
              checked={form.showPhone}
              onChange={onToggle}
            />
            <Check
              label="Show email on profile"
              name="showEmail"
              checked={form.showEmail}
              onChange={onToggle}
            />
          </GroupCard>

          <Field
            label="Contact number"
            name="contactNumber"
            value={form.contactNumber}
            onChange={onChange}
            error={errors.contactNumber}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            Add the number you would like enquiries to use. This can be your
            work number or a number you check regularly.
          </small>

          <Field
            label="Contact email"
            name="email"
            value={form.email}
            onChange={onChange}
            type="email"
            error={errors.email}
          />
          <small className="text-muted d-block mb-3 ppfHelpText">
            Use an email address you are happy to receive enquiries on. You can
            choose whether it shows publicly using the options above.
          </small>
        </>
      );
    }

    if (step === 9) {
      return (
        <GroupCard
          title="Professional registration"
          hint="Add your professional registration details so clients can feel confident in your qualifications."
        >
          {value?.country === "UK" ? (
            <>
              <div className="mb-3 ppfField">
                <label
                  className="form-label ppfLabel"
                  htmlFor="professionalBody"
                >
                  Professional body
                </label>
                <div className="ppfSectionHint">
                  Choose the organisation you are registered with. This will be
                  shown on your profile.
                </div>
                <select
                  id="professionalBody"
                  className={`form-select ppfInput${errors.professionalBody ? " is-invalid" : ""}`}
                  name="professionalBody"
                  value={form.professionalBody || ""}
                  onChange={onChange}
                  required
                >
                  <option value="" disabled>
                    Select your professional body
                  </option>
                  <option value="British Association for Counselling and Psychotherapy (BACP)">
                    British Association for Counselling and Psychotherapy (BACP)
                  </option>
                  <option value="UK Council for Psychotherapy (UKCP)">
                    UK Council for Psychotherapy (UKCP)
                  </option>
                  <option value="British Association for Behavioural and Cognitive Psychotherapies (BABCP)">
                    British Association for Behavioural and Cognitive
                    Psychotherapies (BABCP)
                  </option>
                  <option value="National Counselling and Psychotherapy Society (NCPS)">
                    National Counselling and Psychotherapy Society (NCPS)
                  </option>
                  <option value="Health and Care Professions Council (HCPC)">
                    Health and Care Professions Council (HCPC)
                  </option>
                </select>
                {errors.professionalBody ? (
                  <div className="invalid-feedback">
                    {errors.professionalBody}
                  </div>
                ) : null}
              </div>

              <Field
                label="Registration number"
                name="registrationNumber"
                value={form.registrationNumber}
                onChange={onChange}
                required
                error={errors.registrationNumber}
              />
            </>
          ) : value?.country === "US" ? (
            <>
              <Field
                label="State of registration"
                name="usState"
                value={form.usState}
                onChange={onChange}
                required
                placeholder="eg Nevada"
                error={errors.usState}
              />

              <div className="mb-3 ppfField">
                <label className="form-label ppfLabel" htmlFor="usLicenseType">
                  Licence type
                </label>
                <div className="ppfSectionHint">
                  Choose the licence that best matches your professional
                  qualification.
                </div>
                <select
                  id="usLicenseType"
                  className={`form-select ppfInput${errors.usLicenseType ? " is-invalid" : ""}`}
                  name="usLicenseType"
                  value={form.usLicenseType || ""}
                  onChange={onChange}
                  required
                >
                  <option value="" disabled>
                    Select your licence type
                  </option>
                  <option value="LPC">
                    LPC Licensed Professional Counsellor
                  </option>
                  <option value="LMHC">
                    LMHC Licensed Mental Health Counsellor
                  </option>
                  <option value="LCSW">
                    LCSW Licensed Clinical Social Worker
                  </option>
                  <option value="LMFT">
                    LMFT Licensed Marriage and Family Therapist
                  </option>
                  <option value="Psychologist">Psychologist</option>
                </select>
                {errors.usLicenseType ? (
                  <div className="invalid-feedback">{errors.usLicenseType}</div>
                ) : null}
              </div>

              <Field
                label="Licence number"
                name="usLicenseNumber"
                value={form.usLicenseNumber}
                onChange={onChange}
                required
                error={errors.usLicenseNumber}
              />
            </>
          ) : (
            <div className="ppfSectionHint mb-0">
              Professional registration is not required for your country. You
              can continue without adding these details.
            </div>
          )}
        </GroupCard>
      );
    }

    if (step === 10) {
      return (
        <AddQualification
          user={user}
          error={errors.hasAtLeastOneQualification}
          onHasQualificationChange={(hasAny) => {
            setForm((prev) => ({
              ...prev,
              hasAtLeastOneQualification: !!hasAny,
            }));
            if (hasAny) clearError("hasAtLeastOneQualification");
          }}
        />
      );
    }

    if (step === 11) {
      return (
        <GroupCard
          title="Complete your profile"
          hint="When you complete this form, we will review your profile and let you know when it is ready to go live."
        >
          <div className="ppfSectionHint mb-3">
            You can still go back and edit anything before completing.
          </div>

          <div className="ppfGroupCard mt-2">
            <div className="ppfGroupCardHead">
              <p className="ppfGroupCardTitle mb-0">Ready to submit</p>
            </div>
            <div className="ppfGroupCardBody">
              <div className="ppfSectionHint mb-0">
                Press Complete form to submit your profile for review.
              </div>
            </div>
          </div>
        </GroupCard>
      );
    }

    return null;
  };

  const safeTitle = steps[step]
    ? steps[step].title
    : steps[steps.length - 1].title;
  const safeStepNumber = step + 1 > steps.length ? steps.length : step + 1;

  return (
    <div className="ppfShell">
      <form className="ppfCard" onSubmit={submit}>
        <div className="ppfHead">
          <div className="ppfHeadLeft">
            <h2 className="ppfTitle">{safeTitle}</h2>
            <div className="ppfSubtitle">
              Step {safeStepNumber} of {steps.length}
            </div>
          </div>
          <div className="ppfStepPill">
            {safeStepNumber} of {steps.length}
          </div>
        </div>

        <div className="ppfBody">{renderStep()}</div>

        <hr className="ppfDivider" />

        <div className="ppfFooter">
          <button
            type="button"
            className="btn ppfBtnOutline w-100"
            disabled={step <= 0}
            onClick={handleBack}
          >
            Back
          </button>

          {step < steps.length - 1 ? (
            <button
              type="button"
              className="btn ppfBtnPrimary w-100"
              onClick={handleNext}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="btn ppfBtnPrimary w-100"
              onClick={handleComplete}
            >
              Complete form
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
